/**
 * Customer Portal Login Page — Unified OTP-based login.
 * Single form: email or phone + last 4 SSN → OTP verification.
 * Mobile-first with large touch targets.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import PasswordInput from '../../components/ui/PasswordInput';

type LoginStep = 'credentials' | 'otp';
type PortalLanguage = 'en' | 'es';

interface CustomerLoginPageProps {
  language?: PortalLanguage;
}

const RESEND_COOLDOWN = 30; // seconds

const LOGIN_COPY: Record<
  PortalLanguage,
  {
    portalTitle: string;
    otpTitle: string;
    portalSubtitle: string;
    codeSentVia: string;
    viaEmail: string;
    viaText: string;
    emailOrPhone: string;
    emailOrPhonePlaceholder: string;
    ssnOrAccountLabel: string;
    ssnOrAccountOr: string;
    ssnOrAccountPlaceholder: string;
    verifying: string;
    sendCode: string;
    verifySignIn: string;
    back: string;
    resendIn: string;
    resendCode: string;
    helpFooter: string;
  }
> = {
  en: {
    portalTitle: 'BHPH Account Portal',
    otpTitle: 'Enter Verification Code',
    portalSubtitle: 'View your account and payment history',
    codeSentVia: 'Code sent via',
    viaEmail: 'email',
    viaText: 'text',
    emailOrPhone: 'Email or Phone Number',
    emailOrPhonePlaceholder: 'your@email.com or (555) 123-4567',
    ssnOrAccountLabel: 'Last 4 of SSN',
    ssnOrAccountOr: 'or',
    ssnOrAccountPlaceholder: 'Last 4 digits of SSN or Account #',
    verifying: 'Verifying...',
    sendCode: 'Send Verification Code',
    verifySignIn: 'Verify and Sign In',
    back: 'Back',
    resendIn: 'Resend in',
    resendCode: 'Resend Code',
    helpFooter: 'Need help? Contact the dealership.',
  },
  es: {
    portalTitle: 'Portal de Cuenta BHPH',
    otpTitle: 'Ingresa el Codigo de Verificacion',
    portalSubtitle: 'Consulta tu cuenta e historial de pagos',
    codeSentVia: 'Codigo enviado por',
    viaEmail: 'correo',
    viaText: 'mensaje de texto',
    emailOrPhone: 'Correo o Numero de Telefono',
    emailOrPhonePlaceholder: 'tu@correo.com o (555) 123-4567',
    ssnOrAccountLabel: 'Ultimos 4 del SSN',
    ssnOrAccountOr: 'o',
    ssnOrAccountPlaceholder: 'Ultimos 4 del SSN o # de Cuenta',
    verifying: 'Verificando...',
    sendCode: 'Enviar Codigo de Verificacion',
    verifySignIn: 'Verificar e Iniciar Sesion',
    back: 'Atras',
    resendIn: 'Reenviar en',
    resendCode: 'Reenviar Codigo',
    helpFooter: 'Necesitas ayuda? Contacta al concesionario.',
  },
};

const CustomerLoginPage: React.FC<CustomerLoginPageProps> = ({ language = 'en' }) => {
  const { isAuthenticated, isLoading, requestOTP, verifyOTP } = useCustomerAuth();
  const copy = LOGIN_COPY[language];

  const [step, setStep] = useState<LoginStep>('credentials');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Credentials
  const [identifier, setIdentifier] = useState('');
  const [ssnLastFour, setSsnLastFour] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<'sms' | 'email'>('sms');

  // OTP
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer((t) => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  if (isAuthenticated) {
    return <Navigate to="/customer/dashboard" replace />;
  }

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim() || ssnLastFour.length !== 4) return;
    try {
      setIsSubmitting(true);
      const result = await requestOTP(identifier.trim(), ssnLastFour);
      setDeliveryMethod(result.delivery_method as 'sms' | 'email');
      setStep('otp');
      setResendTimer(RESEND_COOLDOWN);
      // Focus first OTP input
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch {
      // Error handled in context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOTP = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const code = otpDigits.join('');
    if (code.length !== 6) return;
    try {
      setIsSubmitting(true);
      await verifyOTP(identifier.trim(), code);
    } catch {
      // Error handled in context — clear OTP for retry
      setOtpDigits(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);

    // Auto-advance to next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 filled
    if (digit && index === 5) {
      const code = newDigits.join('');
      if (code.length === 6) {
        // Small delay to let state update
        setTimeout(() => handleVerifyOTP(), 50);
      }
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newDigits = [...otpDigits];
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setOtpDigits(newDigits);
    // Focus last filled or first empty
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs.current[focusIdx]?.focus();
    // Auto-submit if complete
    if (pasted.length === 6) {
      setTimeout(() => handleVerifyOTP(), 50);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    try {
      setIsSubmitting(true);
      await requestOTP(identifier.trim(), ssnLastFour);
      setResendTimer(RESEND_COOLDOWN);
      setOtpDigits(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch {
      // Error handled in context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setOtpDigits(['', '', '', '', '', '']);
    setResendTimer(0);
  };

  const maskedIdentifier = identifier.includes('@')
    ? identifier.replace(/(.{2})(.*)(@.*)/, '$1***$3')
    : identifier.replace(/(.{3})(.*)(.{2})/, '$1***$3');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100 py-12 px-4 sm:px-6">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            {step === 'credentials' ? (
              <svg
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            ) : (
              <svg
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {step === 'credentials' ? copy.portalTitle : copy.otpTitle}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {step === 'credentials'
              ? copy.portalSubtitle
              : `${copy.codeSentVia} ${deliveryMethod === 'email' ? copy.viaEmail : copy.viaText} ${maskedIdentifier}`}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
          {/* Step 1: Credentials */}
          {step === 'credentials' && (
            <form onSubmit={handleRequestOTP} className="space-y-5">
              <div>
                <label
                  htmlFor="identifier"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {copy.emailOrPhone}
                </label>
                <input
                  id="identifier"
                  type="text"
                  autoComplete="email tel"
                  placeholder={copy.emailOrPhonePlaceholder}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="ssn" className="block text-sm font-medium text-gray-700 mb-1">
                  {copy.ssnOrAccountLabel}{' '}
                  <span className="text-gray-400 font-normal">{copy.ssnOrAccountOr}</span> BHPH
                  Account #
                </label>
                <PasswordInput
                  id="ssn"
                  inputMode="numeric"
                  maxLength={20}
                  placeholder={copy.ssnOrAccountPlaceholder}
                  value={ssnLastFour}
                  onChange={(e) =>
                    setSsnLastFour(e.target.value.replace(/[^0-9A-Za-z-]/g, '').slice(0, 20))
                  }
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 tracking-widest text-center"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting || isLoading || !identifier.trim() || ssnLastFour.length < 1}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white text-lg font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    {copy.verifying}
                  </span>
                ) : (
                  copy.sendCode
                )}
              </button>
            </form>
          )}

          {/* Step 2: OTP Verification */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              {/* 6-digit OTP input */}
              <div className="flex justify-center gap-2 sm:gap-3">
                {otpDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      inputRefs.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOTPChange(i, e.target.value)}
                    onKeyDown={(e) => handleOTPKeyDown(i, e)}
                    onPaste={i === 0 ? handleOTPPaste : undefined}
                    className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    autoComplete="one-time-code"
                  />
                ))}
              </div>

              <button
                type="submit"
                disabled={isSubmitting || isLoading || otpDigits.join('').length !== 6}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white text-lg font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    {copy.verifying}
                  </span>
                ) : (
                  copy.verifySignIn
                )}
              </button>

              {/* Resend + Back */}
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleBackToCredentials}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  &larr; {copy.back}
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendTimer > 0 || isSubmitting}
                  className={`font-medium transition-colors ${
                    resendTimer > 0
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-blue-600 hover:text-blue-800'
                  }`}
                >
                  {resendTimer > 0 ? `${copy.resendIn} ${resendTimer}s` : copy.resendCode}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-gray-400">{copy.helpFooter}</p>
      </div>
    </div>
  );
};

export default CustomerLoginPage;
